import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Copy, Loader2, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { ProtocolView } from "@/components/ProtocolView";
import { LockedAI, MeetingStatus, SectionLabel, StatusBadge } from "@/components/Status";
import { useAuth } from "@/context/AuthContext";
import { LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

export default function MeetingDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [meeting, setMeeting] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const selected = params.get("job");

  const loadJobs = useCallback(async () => {
    const r = await api.get(`/meetings/${id}/jobs`);
    setJobs(r.data);
    return r.data;
  }, [id]);

  useEffect(() => {
    api.get(`/meetings/${id}`).then((r) => setMeeting(r.data)).catch((e) => toast.error(t(`errors.${errCode(e)}`, errCode(e))));
    loadJobs().then((list) => {
      if (!selected && list[0]) setParams({ job: list[0].id }, { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!selected) {
      setJob(null);
      return undefined;
    }
    let active = true;
    const tick = async () => {
      try {
        const r = await api.get(`/ai/jobs/${selected}`);
        if (active) setJob(r.data);
        return r.data;
      } catch {
        return null;
      }
    };
    tick();
    const iv = setInterval(async () => {
      const j = await tick();
      if (j && ["done", "error"].includes(j.status)) {
        clearInterval(iv);
        loadJobs().catch(() => {});
      }
    }, 3000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [selected, loadJobs]);

  const copyGuest = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${id}?code=${meeting.join_code}`);
    toast.success(t("common.copied"));
  };
  const removeJob = async (j) => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    await api.delete(`/ai/jobs/${j.id}`);
    const list = await loadJobs();
    setParams(list[0] ? { job: list[0].id } : {}, { replace: true });
  };

  return (
    <div className="min-h-screen" data-testid="meeting-detail-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <Link to="/dashboard" className="mb-8 inline-flex items-center gap-2 text-sm text-vox-dim hover:text-vox-text" data-testid="back-to-dashboard"><ArrowLeft size={14} /> {t("nav.dashboard")}</Link>
        {!meeting ? (
          <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div>
        ) : (
          <>
            <div className="reveal mb-12 flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="mb-3 flex items-center gap-3"><span className="mono-label">{t("detail.title")}</span><MeetingStatus status={meeting.status} /></div>
                <h1 className="text-3xl font-semibold tracking-tight text-vox-text sm:text-5xl" data-testid="meeting-detail-title">{meeting.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                  <span>{meeting.language}</span>
                  <span>{new Date(meeting.scheduled_at || meeting.created_at).toLocaleString(LOCALE[lang])}</span>
                </div>
                {meeting.description && <p className="mt-4 max-w-2xl text-sm text-vox-muted">{meeting.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={copyGuest} className="btn-ghost" data-testid="detail-copy-guest-link"><Copy size={14} /> {t("dashboard.guestLink")}</button>
                {meeting.status !== "ended" && <Link to={`/room/${id}`} className="btn-primary" data-testid="detail-join-button"><Video size={14} /> {t("dashboard.join")}</Link>}
              </div>
            </div>

            <SectionLabel index="01">{t("detail.recordings")}</SectionLabel>
            {!user.ai_enabled ? <LockedAI /> : jobs.length === 0 ? (
              <div className="panel border-dashed p-12 text-center text-sm text-vox-dim" data-testid="jobs-empty">{t("detail.noJobs")}</div>
            ) : (
              <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
                <ul className="space-y-2" data-testid="jobs-list">
                  {jobs.map((j) => (
                    <li key={j.id} className={`panel flex items-center justify-between gap-3 p-4 transition-colors ${selected === j.id ? "border-vox-accent" : "hover:border-vox-accent/50"}`} data-testid="job-list-item">
                      <button type="button" onClick={() => setParams({ job: j.id })} className="min-w-0 flex-1 text-left" data-testid="job-select-button">
                        <div className="truncate text-sm text-vox-text">{t(`detail.source.${j.source}`, j.source)}</div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-vox-dim">{new Date(j.created_at).toLocaleString(LOCALE[lang])}</div>
                        <div className="mt-2"><StatusBadge status={j.status} /></div>
                      </button>
                      <button type="button" onClick={() => removeJob(j)} className="rounded-full p-2 text-vox-dim transition-colors hover:bg-vox-rec/10 hover:text-[#ff9b9e]" data-testid="job-delete-button"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
                <div>{job ? <ProtocolView job={job} onChanged={(patch) => setJob((j) => ({ ...j, ...patch }))} /> : <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div>}</div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
